import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import "../styles/components/DialogProvider.css";

const DialogContext = createContext(null);

const getDefaultDialogConfig = (type) => {
    if (type === "prompt") {
        return {
            type: "prompt",
            title: "Input Required",
            message: "",
            confirmText: "Submit",
            cancelText: "Cancel",
            placeholder: "",
            defaultValue: "",
        };
    }

    if (type === "confirm") {
        return {
            type: "confirm",
            title: "Please Confirm",
            message: "",
            confirmText: "Confirm",
            cancelText: "Cancel",
        };
    }

    return {
        type: "alert",
        title: "Notice",
        message: "",
        confirmText: "OK",
    };
};

export const DialogProvider = ({ children }) => {
    const [dialogQueue, setDialogQueue] = useState([]);
    const [activeDialog, setActiveDialog] = useState(null);
    const confirmButtonRef = useRef(null);
    const inputRef = useRef(null);

    const queueDialog = useCallback((dialogConfig) => {
        return new Promise((resolve) => {
            setDialogQueue((prevQueue) => [
                ...prevQueue,
                {
                    ...dialogConfig,
                    resolve,
                },
            ]);
        });
    }, []);

    useEffect(() => {
        if (activeDialog || dialogQueue.length === 0) return;

        const [nextDialog, ...remainingDialogs] = dialogQueue;
        const mergedDialog = {
            ...getDefaultDialogConfig(nextDialog.type),
            ...nextDialog,
        };

        setDialogQueue(remainingDialogs);
        setActiveDialog(mergedDialog);
    }, [activeDialog, dialogQueue]);

    useEffect(() => {
        if (!activeDialog) return;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = previousOverflow || "auto";
        };
    }, [activeDialog]);

    useEffect(() => {
        if (!activeDialog) return;

        if (activeDialog.type === "prompt") {
            inputRef.current?.focus();
            return;
        }

        confirmButtonRef.current?.focus();
    }, [activeDialog]);

    const closeDialog = useCallback(
        (value) => {
            if (!activeDialog) return;
            activeDialog.resolve(value);
            setActiveDialog(null);
        },
        [activeDialog]
    );

    const handleConfirm = useCallback(() => {
        if (!activeDialog) return;

        if (activeDialog.type === "prompt") {
            closeDialog(activeDialog.defaultValue);
            return;
        }

        closeDialog(true);
    }, [activeDialog, closeDialog]);

    const handleCancel = useCallback(() => {
        if (!activeDialog) return;

        if (activeDialog.type === "confirm") {
            closeDialog(false);
            return;
        }

        if (activeDialog.type === "prompt") {
            closeDialog(null);
            return;
        }

        closeDialog(true);
    }, [activeDialog, closeDialog]);

    useEffect(() => {
        if (!activeDialog) return;

        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                handleCancel();
            }

            if (event.key === "Enter") {
                handleConfirm();
            }
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [activeDialog, handleCancel, handleConfirm]);

    const showAlert = useCallback(
        (message, options = {}) => {
            return queueDialog({
                type: "alert",
                message,
                ...options,
            });
        },
        [queueDialog]
    );

    const showPrompt = useCallback(
        (message, options = {}) => {
            return queueDialog({
                type: "prompt",
                message,
                defaultValue: options.defaultValue || "",
                ...options,
            });
        },
        [queueDialog]
    );

    const showConfirm = useCallback(
        (message, options = {}) => {
            return queueDialog({
                type: "confirm",
                message,
                ...options,
            });
        },
        [queueDialog]
    );

    const dialogValue = useMemo(
        () => ({
            showAlert,
            showPrompt,
            showConfirm,
        }),
        [showAlert, showConfirm, showPrompt]
    );

    return (
        <DialogContext.Provider value={dialogValue}>
            {children}
            {activeDialog && (
                <section id="custom-dialog">
                    <div className="custom-dialog-overlay" onClick={handleCancel}>
                        <div
                            className={`custom-dialog-container dialog-${activeDialog.type}`}
                            onClick={(event) => event.stopPropagation()}
                            role="dialog"
                            aria-modal="true"
                            aria-label={activeDialog.title}
                        >
                            <div className="custom-dialog-titlebar">
                                <span className={`custom-dialog-chip chip-${activeDialog.type}`}>
                                    {activeDialog.type === "prompt"
                                        ? "Prompt"
                                        : activeDialog.type === "confirm"
                                            ? "Confirm"
                                            : "Alert"}
                                </span>
                                <span className="custom-dialog-window-title">
                                    {activeDialog.type === "prompt"
                                        ? "Input Dialog"
                                        : activeDialog.type === "confirm"
                                            ? "Confirmation Dialog"
                                            : "System Alert"}
                                </span>
                            </div>
                            <div className="custom-dialog-content">
                                <div className="custom-dialog-body">
                                    <span
                                        className={`custom-dialog-type-icon icon-${activeDialog.type}`}
                                        aria-hidden="true"
                                    >
                                        {activeDialog.type === "prompt"
                                            ? "?"
                                            : activeDialog.type === "confirm"
                                                ? "!"
                                                : "i"}
                                    </span>
                                    <div className="custom-dialog-copy">
                                        <h3>{activeDialog.title}</h3>
                                        <p>{activeDialog.message}</p>
                                    </div>
                                </div>
                                {activeDialog.type === "prompt" && (
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        value={activeDialog.defaultValue}
                                        onChange={(event) =>
                                            setActiveDialog((prevDialog) => ({
                                                ...prevDialog,
                                                defaultValue: event.target.value,
                                            }))
                                        }
                                        placeholder={activeDialog.placeholder}
                                    />
                                )}
                                <div className="custom-dialog-actions">
                                    {(activeDialog.type === "confirm" || activeDialog.type === "prompt") && (
                                        <button
                                            className="custom-dialog-button secondary"
                                            onClick={handleCancel}
                                            type="button"
                                        >
                                            {activeDialog.cancelText}
                                        </button>
                                    )}
                                    <button
                                        className="custom-dialog-button primary"
                                        onClick={handleConfirm}
                                        ref={confirmButtonRef}
                                        type="button"
                                    >
                                        {activeDialog.confirmText}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}
        </DialogContext.Provider>
    );
};

export const useDialog = () => {
    const context = useContext(DialogContext);

    if (!context) {
        throw new Error("useDialog must be used inside DialogProvider.");
    }

    return context;
};
